import {
  AnimationAction,
  AnimationClip,
  AnimationMixer,
  Camera,
  LoopOnce,
  LoopRepeat,
} from 'three';

import BaseControls from '../BaseControls/index.js';

export interface AnimatedCameraControlsEventMap {
  loop: { action: AnimationAction; loopDelta: number };
  finished: { action: AnimationAction; direction: number };
  play: { action: AnimationAction };
  abort: { reason: string; event: any };
}

/**
 * Provides a way to animate a camera using keyframes.
 */
export class AnimatedCameraControls extends BaseControls<AnimatedCameraControlsEventMap> {
  public mixer: AnimationMixer | null = null;
  public clip: AnimationClip | null = null;
  public action: AnimationAction | null = null;
  public status: 'playing' | 'stopped' | 'idle' | 'pause' = 'idle';
  protected _loop: boolean = false;

  get isFinished() {
    return this.mixer && this.clip ? this.mixer.time === this.clip.duration : false;
  }

  get isRunning() {
    return !!this.action?.isRunning();
  }

  set loop(loop: boolean) {
    if (this.action) {
      this.action.loop = loop ? LoopRepeat : LoopOnce;
    }
  }

  constructor(camera: Camera, domElement?: HTMLElement) {
    super(camera, domElement);
    this.dispatchEvent = this.dispatchEvent.bind(this);
  }

  setClip(clip: AnimationClip) {
    if (this.mixer) {
      this.mixer.removeEventListener('finished', this.dispatchEvent);
      this.mixer.removeEventListener('loop', this.dispatchEvent);
    }
    this.mixer = new AnimationMixer(this._camera);
    this.mixer.addEventListener('finished', this.dispatchEvent);
    this.mixer.addEventListener('loop', this.dispatchEvent);
    this.clip = clip;
  }

  /**
   * Play the animation at the current time.
   */
  play(restart?: boolean) {
    if (!this.mixer) {
      return;
    }
    if (this.clip) {
      if (restart) {
        this.stop();
      }

      this.action = this.mixer.clipAction(this.clip).play();
      this.loop = this._loop;
      this.status = 'playing';
      this.dispatchEvent({ type: 'play', action: this.action });
    }
  }

  /**
   * Pause the animation keeping the current time.
   */
  pause() {
    if (this.mixer && this.status === 'playing') {
      this.mixer.stopAllAction();
      this.status = 'pause';
    }
  }
  /**
   * Stop the animation. This will reset the time to 0.
   */
  stop() {
    if (this.mixer && (this.status === 'playing' || this.status === 'pause')) {
      this.mixer.stopAllAction();
      this.mixer.setTime(0);
      this.status = 'stopped';
    }
  }

  /**
   * Update the camera controls.
   */
  update(delta: number) {
    if (this.mixer && this.status === 'playing') {
      this.mixer.update(delta);
      return true;
    }

    return false;
  }

  protected _onEventDown = (evt: PointerEvent) => {
    if (this.status === 'playing' && evt.button === 0) {
      this.dispatchEvent({ type: 'abort', reason: 'pointer', event: evt });
    }
  };

  protected _onKeyDown = (evt: KeyboardEvent) => {
    if (evt.code === 'Escape' && this.status === 'playing') {
      this.dispatchEvent({ type: 'abort', reason: 'escape', event: evt });
    }
  };

  connect(domElement: HTMLElement): void {
    this._domElement = domElement;
    this._domElement.addEventListener('pointerdown', this._onEventDown);
    this._domElement.ownerDocument.addEventListener('keydown', this._onKeyDown);
  }

  disconnect(): void {
    if (this._domElement) {
      this._domElement.removeEventListener('pointerdown', this._onEventDown);
      this._domElement.ownerDocument.removeEventListener('keydown', this._onKeyDown);
    }
  }

  dispose() {
    this.disconnect();
    this.mixer?.stopAllAction();
    this.mixer?.uncacheAction(this.clip!);
    this.mixer?.uncacheClip(this.clip!);
  }
}
